import { FuuzeColors, type SpotlightLink, tryCatch } from '@fuuze/shared';
import type { Palette } from '@vibrant/color';
import { AnimatePresence, motion } from 'motion/react';
import { Vibrant } from 'node-vibrant/worker';
import { useEffect, useState } from 'react';
import { FiExternalLink } from 'react-icons/fi';
import { firebaseUtil } from '../../utils/firebase.util';
import { FuuzeImage } from '../fuuze-image';
import styles from './player-spotlight-preview-banner.module.css';

type Props = {
  spotlight?: SpotlightLink | null;
  wrapperStyles?: React.CSSProperties;
};

export function PlayerSpotlightPreviewBanner({ spotlight, wrapperStyles }: Props) {
  const [previewImagePalette, setPreviewImagePalette] = useState<Palette | undefined>(undefined);

  useEffect(() => {
    if (!spotlight?.preview?.imageSizes) return;

    (async () => {
      let result = await tryCatch(Vibrant.from(spotlight.preview!.imageSizes!.md!).getPalette());
      if (result.data) {
        setPreviewImagePalette(result.data);
        return;
      }

      const token = await firebaseUtil().getIdToken();
      result = await tryCatch(
        Vibrant.from(
          `${import.meta.env.VITE_BACKEND_URL}/image-proxy?url=${spotlight.preview?.imageSizes?.md}&token=${token}`,
        ).getPalette(),
      );

      if (result.data) {
        setPreviewImagePalette(result.data);
        return;
      }
    })();
  }, [spotlight?.preview?.imageSizes]);

  return (
    <AnimatePresence>
      {spotlight?.preview && (
        <motion.a
          href={spotlight.url}
          target='_blank'
          className={styles.banner}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          style={{
            ...wrapperStyles,
            backgroundColor: `color-mix(in oklab, ${previewImagePalette?.Vibrant?.hex || FuuzeColors.orange.orange100}, black 70%)`,
            borderColor: `color-mix(in oklab, ${previewImagePalette?.Vibrant?.hex || FuuzeColors.orange.orange100}, black 50%)`,
          }}
        >
          {spotlight.preview.imageSizes && (
            <div className={styles.imageWrapper}>
              <FuuzeImage
                size='sm'
                imageSizes={spotlight.preview.imageSizes}
                alt={spotlight.preview.title || 'spotlight preview'}
              />
            </div>
          )}

          <div className={styles.contentWrapper}>
            {spotlight.preview.siteName && <span className={styles.siteName}>{spotlight.preview.siteName}</span>}
            {spotlight.preview.title && <span className={styles.title}>{spotlight.preview.title}</span>}
            {spotlight.preview.description && (
              <span className={styles.description}>{spotlight.preview.description}</span>
            )}
          </div>

          <div className={styles.iconWrapper}>
            <FiExternalLink size={16} />
          </div>
        </motion.a>
      )}
    </AnimatePresence>
  );
}
